import { useState, type KeyboardEvent } from 'react';
import { Chip, Input } from '@vnetwork/ui/primitives';
import styles from './ChipListInput.module.css';

/**
 * Freeform chip/tag input for a list of short values (IP/CIDR, GEO codes, …).
 * Type a value and press Enter or comma to add it as a chip; each chip has a ×
 * to remove it — so a value in the middle of the list can be deleted directly,
 * instead of editing a comma-separated string. Controlled: every change calls
 * `onChange` with the next full array.
 */
export function ChipListInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    // Support pasting/typing several comma-separated values at once.
    const parts = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraft('');
  };

  const remove = (v: string) => onChange(value.filter((x) => x !== v));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div className={styles.wrap}>
      {value.length > 0 && (
        <div className={styles.chips}>
          {value.map((v) => (
            <Chip key={v} size="sm" appearance="subtle" onRemove={() => remove(v)} removeLabel={`Remove ${v}`}>
              {v}
            </Chip>
          ))}
        </div>
      )}
      <Input
        value={draft}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && add(draft)}
      />
    </div>
  );
}
